import { useState, type FormEvent } from 'react';
import { api, ProblemError, UnauthorizedError } from './api';
import Hint from './Hint';

interface Props {
  onSessionLost: () => void;
}

const ISSUE_LABELS: Record<string, string> = {
  PASSWORD_INCORRECT: 'كلمة السر الحالية غير صحيحة',
  PASSWORD_UNCHANGED: 'الجديدة لازم تختلف عن الحالية',
};

/** تغيير كلمة السر — يُخرج كل الأجهزة الأخرى، وهذا الجهاز يبقى داخلاً. */
export default function ChangePassword({ onSessionLost }: Props) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch = confirm !== '' && confirm !== newPassword;
  const tooShort = newPassword !== '' && newPassword.length < 12;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (mismatch || tooShort || !currentPassword || !newPassword) return;
    setBusy(true);
    setFailure(null);
    setDone(false);
    try {
      await api.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      setDone(true);
    } catch (caught) {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        const issues = caught.problem.issues ?? [];
        setFailure(
          issues.length > 0
            ? issues.map((issue) => ISSUE_LABELS[issue.code] ?? issue.message).join('، ')
            : (caught.problem.detail ?? caught.problem.title),
        );
      } else {
        setFailure(caught instanceof Error ? caught.message : String(caught));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
      <h2>تغيير كلمة السر</h2>
      <div className="form-row">
        <label className="grow">
          الحالية
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </label>
      </div>
      <div className="form-row">
        <label className="grow">
          الجديدة (١٢ محرفًا على الأقل)
          <input
            type="password"
            autoComplete="new-password"
            className={tooShort ? 'invalid' : ''}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          <Hint>جملة طويلة تتذكرها أفضل من كلمة معقدة. بعد التغيير تخرج كل الأجهزة الأخرى.</Hint>
        </label>
        <label className="grow">
          تأكيد الجديدة
          <input
            type="password"
            autoComplete="new-password"
            className={mismatch ? 'invalid' : ''}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </label>
      </div>
      {mismatch && <p className="error">التأكيد لا يطابق الجديدة</p>}
      {failure && <p className="error">{failure}</p>}
      {done && <p className="sub">تغيّرت كلمة السر، وخرجت كل الأجهزة الأخرى.</p>}
      <div className="form-actions">
        <button
          type="submit"
          className="primary"
          disabled={busy || mismatch || tooShort || !currentPassword || !newPassword || !confirm}
        >
          {busy ? 'جارى الحفظ…' : 'تغيير'}
        </button>
      </div>
    </form>
  );
}
